import './UfTile.css';
import { FALLBACK_PARTY, type Party, partyClass } from '../theme/parties';
import { Icon } from './Icon';

/** Fundo do selo (§3.8): cor cheia do líder, degraus de margem de governador, neutro, "sem dados" ou "sem disputa". */
export type UfTileFill = 'leader' | 'm0' | 'm1' | 'm3' | 'plain' | 'nodata' | 'norace';
/** `default` 30×22 · `mini` 24×18 · `row` 34×24 · `big` 34×26 · `strip` (grade de Governadores) altura 24/32. */
export type UfTileSize = 'default' | 'mini' | 'row' | 'big' | 'strip';

const PARTY_FILLS: ReadonlySet<UfTileFill> = new Set(['leader', 'm0', 'm1', 'm3']);

interface UfTileBase {
  /**
   * Sigla da UF (`PlaceCode`), ISO-2 do país ou número da zona; "ZZ" (Exterior) desenha o globo. Tipo próprio (string):
   * `components/` não importa `contracts/base` (FE §4.2).
   */
  code: string;
  fill: UfTileFill;
  /** Partido dos fills de partido (leader, m0, m1, m3); sem ele, a cor de reserva. */
  party?: Party;
  size?: UfTileSize;
}
interface UfTileClickable {
  /** Clicável (grade de Governadores, §11.1): vira `<button>`. */
  onClick: () => void;
  /** Nome acessível do botão (§11.1): "{UF por extenso}, {Nome} {pct}" ou "{UF}, aguardando". */
  label: string;
  /** UF aberta (`aria-current="true"`). */
  current?: boolean;
}
interface UfTileDecorative {
  onClick?: undefined;
  label?: undefined;
  current?: undefined;
}
export type UfTileProps = UfTileBase & (UfTileClickable | UfTileDecorative);

/** Selo de UF (§3.8): sigla, ISO, zona ou globo numa caixa colorida pelo estado da disputa. */
export function UfTile({ code, fill, party, size = 'default', onClick, label, current }: UfTileProps) {
  const cls = [
    'uf-tile',
    `uf-tile--${size}`,
    `uf-tile--${fill}`,
    PARTY_FILLS.has(fill) && partyClass(party ?? FALLBACK_PARTY),
  ]
    .filter(Boolean)
    .join(' ');
  const content = code === 'ZZ' ? <Icon name="globe" size={12} /> : code;

  if (onClick) {
    return (
      <button
        type="button"
        class={cls}
        data-testid="uf-tile"
        data-dataviz=""
        aria-label={label}
        aria-current={current ? 'true' : undefined}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }
  return (
    <span class={cls} data-testid="uf-tile" data-dataviz="" aria-hidden="true">
      {content}
    </span>
  );
}
